import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import "./ProductGroupSelect.css";

const MAX_RENDER = 50;

// The box (border/padding/background) is inline, not a CSS class: a global
// ".form-group input" rule elsewhere in the app has higher specificity than a
// plain class here and would silently re-box the raw <input> whenever this
// component is dropped inside a ".form-group" wrapper (border+padding+
// background applied twice — once by .pg-select-control, once by that global
// rule — producing a box-inside-a-box next to the search icon). Inline styles
// always win over any external stylesheet rule, so the look is guaranteed
// regardless of what page embeds this.
const controlStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  height: 36,
  padding: "0 10px",
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  boxSizing: "border-box",
};
const controlDisabledStyle = {
  background: "#f3f4f6",
  cursor: "not-allowed",
};
const inputStyle = {
  flex: 1,
  minWidth: 0,
  width: "100%",
  height: "100%",
  padding: 0,
  border: "none",
  outline: "none",
  background: "none",
  fontSize: 13,
  color: "#0f172a",
};

// Searchable combobox for picking a product category (~170+ rows in this app,
// too many for a plain <select>). Styling is self-contained (own CSS file, own
// class names) rather than reusing another page's classes, so this drops into
// any form/filter bar without depending on ambient styles being present.
export default function ProductGroupSelect({
  groups = [],
  value,
  onChange,
  placeholder,
  allowEmptyLabel,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  const selected = useMemo(
    () => groups.find((g) => String(g.product_group_id) === String(value)) || null,
    [groups, value],
  );

  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? groups.filter((g) =>
          String(g.product_group_description || g.product_group || "")
            .toLowerCase()
            .includes(q),
        )
      : groups;
    return list.slice(0, MAX_RENDER);
  }, [groups, query]);

  const pick = (productGroupId) => {
    onChange(productGroupId);
    setOpen(false);
    setQuery("");
  };

  const selectedLabel = selected
    ? selected.product_group_description || selected.product_group || ""
    : "";

  const dynamicControlStyle = {
    ...controlStyle,
    ...(focused
      ? { borderColor: "#2563eb", boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.12)" }
      : {}),
    ...(disabled ? controlDisabledStyle : {}),
  };

  return (
    <div className="pg-select" ref={ref}>
      <div style={dynamicControlStyle}>
        <Search size={16} className="pg-select-icon" />
        <input
          type="text"
          className="pg-select-input"
          style={inputStyle}
          placeholder={open ? "" : selectedLabel || placeholder}
          value={open ? query : ""}
          disabled={disabled}
          onFocus={() => {
            if (disabled) return;
            setFocused(true);
            setOpen(true);
            setQuery("");
          }}
          onBlur={() => setFocused(false)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
        />
        {selected && !disabled && (
          <button
            type="button"
            className="pg-select-clear"
            onClick={() => pick("")}
            aria-label="Clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="pg-select-list">
          {allowEmptyLabel && (
            <button
              type="button"
              className="pg-select-item"
              onMouseDown={(event) => {
                event.preventDefault();
                pick("");
              }}
            >
              <span className="pg-select-item-name">{allowEmptyLabel}</span>
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="pg-select-empty">No categories found</div>
          ) : (
            filtered.map((group) => (
              <button
                key={group.product_group_id}
                type="button"
                className="pg-select-item"
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(String(group.product_group_id));
                }}
              >
                <span className="pg-select-item-name">
                  {group.product_group_description || group.product_group}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
